import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactElement } from 'react';
import { useAuth } from './auth/AuthProvider';
import { Layout, Spinner } from './components/Layout';
import { BrandsPage } from './pages/BrandsPage';
import { BrandPage } from './pages/BrandPage';
import { ModelPage } from './pages/ModelPage';
import { VariantPage } from './pages/VariantPage';
import { PublishPage } from './pages/PublishPage';
import { UsersPage } from './pages/UsersPage';
import { CallbackPage, LoginPage, NotFoundPage } from './pages/AuthPages';

/**
 * Client-side gating is a convenience, not a control — every route's data comes
 * from an API that independently verifies the token and the caller's group.
 */
function RequireAuth({ children, adminOnly = false }: { children: ReactElement; adminOnly?: boolean }) {
  const { user, ready } = useAuth();
  const location = useLocation();

  if (!ready) return <Spinner />;
  if (!user) return <Navigate to="/" replace state={{ from: location.pathname }} />;
  if (adminOnly && !user.isAdmin) return <Navigate to="/brands" replace />;
  return children;
}

export function App() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/callback" element={<CallbackPage />} />
      <Route path="/" element={user ? <Navigate to="/brands" replace /> : <LoginPage />} />

      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route path="/brands" element={<BrandsPage />} />
        <Route path="/brands/:brandId" element={<BrandPage />} />
        <Route path="/brands/:brandId/models/:modelId" element={<ModelPage />} />
        <Route
          path="/brands/:brandId/models/:modelId/variants/:variantId"
          element={<VariantPage />}
        />
        <Route path="/publish" element={<PublishPage />} />
        <Route
          path="/users"
          element={
            <RequireAuth adminOnly>
              <UsersPage />
            </RequireAuth>
          }
        />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
